import { ArrowForwardIos } from '@mui/icons-material';
import { Divider, ListItemButton, Paper, Stack, Typography } from '@mui/material';
import { formBySubmissionRoute } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useFormDefinitionQuery, useLatestSubmittedFormSubmissionByPatientAndForm, useRequestAdministeredSubmissionMutation } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { ProgressIcon, useAlert } from '@uplift/ui-v2';
import React from 'react';
import { useHistory } from 'react-router';
import { useAssessmentKeysList } from './hooks';


const AssessmentItem: React.VFC<{ formId: string; patientId: string }> = (props) => {
  const timezone = useTimezone();
  const history = useHistory();
  const alert = useAlert();
  const { data: formDefinition } = useFormDefinitionQuery(props.formId);
  const { data: latestSubmittedSubmissionDetails } = useLatestSubmittedFormSubmissionByPatientAndForm({ patientId: props.patientId, formId: props.formId });
  const requestAdministeredSubmissionMutation = useRequestAdministeredSubmissionMutation();
  const [ isLoading, setIsLoading ] = React.useState(false);

  return (
    <Stack
      disabled={isLoading}
      component={ListItemButton}
      direction="row"
      alignItems="center"
      p={3}
      sx={{ height: '100px' }}
      onClick={async () => {
        try {
          setIsLoading(true);
          const response = await requestAdministeredSubmissionMutation.mutateAsync({
            patientId: props.patientId,
            formId: props.formId,
          });

          setIsLoading(false);
          history.push(formBySubmissionRoute.createLink({ submissionId: response.data.submissionId }));
        } catch {
          alert.error('Something went wrong. Please try again later');
        } finally {
          setIsLoading(false);
        }
      }}
    >
      <Stack flex={1}>
        <Typography fontWeight={500}>{formDefinition.name}</Typography>
      </Stack>
      <Stack direction="row" gap={1} alignItems="center">
        {latestSubmittedSubmissionDetails ? (
          <Typography component="span" variant="body2">Last completed {CalendarDateService.date(latestSubmittedSubmissionDetails.completedAt).tz(timezone).format('MMM D')}</Typography>
        ) : (
          <Typography component="span" variant="body2">No submissions</Typography>
        )}
        {isLoading ? <ProgressIcon size={12} /> : <ArrowForwardIos sx={{ fontSize: '12px' }} />}
      </Stack>
    </Stack>
  );
};


export const AdministerTab: React.VFC<{ patientId: string }> = (props) => {
  const items = useAssessmentKeysList();

  return (
    <Stack gap={2} sx={{ p: 2 }}>
      <Typography variant="body2">
        Answers are recorded as clinician-administered.
      </Typography>

      <Paper variant="outlined">
        {items.map((item, index) => (
          <React.Fragment key={item.formId}>
            {index !== 0 && <Divider />}
            <AssessmentItem patientId={props.patientId} formId={item.formId} />
          </React.Fragment>
        ))}
      </Paper>
    </Stack>
  );
};
